import React, { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '../../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { APIResponseData, blogService } from '../../services/bolgUtilsApi';
import { LoadingSpinner } from '../../../../components/ui/loading-spinner';
import { Card } from '../../../../components/ui/card';

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedText: string;
  onApplyToBlog?: (newText: string) => void;
  setSelectedText: (text: string) => void;
}

export function ReviewModal({
  isOpen,
  onClose,
  selectedText,
  onApplyToBlog,
  setSelectedText,
}: ReviewModalProps) {
  const [loading, setLoading] = useState(false);
  const [selectedLength, setSelectedLength] = useState('');
  const [selectedTone, setSelectedTone] = useState('');
  const [modifiedText, setModifiedText] = useState<
    string | APIResponseData<string>
  >('');
  const [error, setError] = useState('');

  const handleLengthChange = (value: string) => {
    setSelectedLength(value);
    setSelectedTone('');
  };

  const handleToneChange = (value: string) => {
    setSelectedTone(value);
    setSelectedLength('');
  };

  const handleApply = async () => {
    try {
      setLoading(true);
      setError('');

      if (selectedLength) {
        const response = await blogService.changeLength(
          selectedText,
          selectedText,
          'neutral',
          selectedLength
        );
        if (response) {
          setModifiedText(response);
        }
      }

      if (selectedTone) {
        const response = await blogService.changeTone(
          selectedText,
          selectedText,
          selectedTone
        );
        if (response?.data) {
          setModifiedText(response.data);
        }
      }
    } catch (err) {
      console.error(err);
      setError('Failed to modify text. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGrammarCheck = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await blogService.checkGrammar(selectedText);
      if (response?.data) {
        if (typeof response.data === 'string') {
          setModifiedText(response.data);
        } else if ('data' in response.data) {
          setModifiedText(response.data.data);
        }
      }
    } catch (err) {
      console.error(err);
      setError('Failed to check grammar. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyToBlog = () => {
    if (modifiedText && onApplyToBlog) {
      onApplyToBlog(modifiedText as string);
      handleClose();
    } else {
      onApplyToBlog?.(selectedText as string);
    }
  };

  const handleClose = () => {
    setSelectedLength('');
    setSelectedTone('');
    setModifiedText('');
    setError('');
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedLength('');
      setSelectedTone('');
      setModifiedText('');
      setError('');
    }
  }, [isOpen]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/30" />
        <Dialog.Content
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px]  lg:w-[28vw] p-4 shadow-lg  bg-background rounded-lg max-h-[90vh] overflow-y-auto focus:outline-none text-text-primary"
          onClick={(e) => e.stopPropagation()}
        >
          <Dialog.Title className="text-lg text-primary font-medium mb-4">
            Modify Text
          </Dialog.Title>

          {!modifiedText ? (
            <Card className="p-3  mb-4">
              <p className="text-sm text-primary font-medium">Selected Text:</p>
              <textarea
                value={selectedText}
                onChange={(e) => setSelectedText(e.target.value)}
                onMouseUp={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                className="text-sm mt-1 w-full h-[120px] border-none focus:border-none rounded-md break-words resize-none bg-background"
              />
            </Card>
          ) : (
            <Card className="p-3  mb-4">
              <p className="text-sm font-medium">Modified Text:</p>
              <textarea
                value={modifiedText as string}
                onChange={(e) => setModifiedText(e.target.value)}
                onMouseUp={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                className="text-sm mt-1 w-full h-[120px] border-none focus:border-none rounded-md break-words resize-none"
              />
            </Card>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex-col">
                <label className="text-sm font-medium">Change Length</label>
                <Select
                  onValueChange={handleLengthChange}
                  value={selectedLength}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select length change" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Increase">Increase Length</SelectItem>
                    <SelectItem value="Decrease">Decrease Length</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="text-center text-sm text-primary my-2">or</div>

              <div className="flex-col">
                <label className="text-sm font-medium">Change Tone</label>
                <Select onValueChange={handleToneChange} value={selectedTone}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select tone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Professional">Professional</SelectItem>
                    <SelectItem value="Casual">Casual</SelectItem>
                    <SelectItem value="Formal">Formal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Button
                onClick={handleGrammarCheck}
                size="sm"
                variant="customSecondary"
                className="w-full bg-secondary"
              >
                Check Grammar
              </Button>
            </div>

            <Button
              onClick={handleApply}
              disabled={(!selectedLength && !selectedTone) || loading}
              variant="customSecondary"
              className="w-full bg-secondary"
            >
              {loading ? <LoadingSpinner /> : 'Apply Changes'}
            </Button>

            {error && <div className="text-red-500 text-sm mt-2">{error}</div>}

            {/* {modifiedText && ( */}
            <Button
              onClick={handleApplyToBlog}
              variant="customSecondary"
              className="w-full bg-secondary"
            >
              Apply Changes to Blog
            </Button>
            {/* )} */}
          </div>

          <Dialog.Close asChild>
            <button
              className="absolute top-3 right-3 text-primary "
              aria-label="Close"
            >
              ✕
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default ReviewModal;
